import type * as Y from 'yjs';
import type { CommandManager } from '../../../core/commands/CommandManager';
import type { Edge, Node } from '../../../core/models/scene';
import type { Point2D } from '../types/layout.types';

type RequestFn = (input: string, init?: RequestInit) => Promise<Response>;

const MAX_PROMPT_PEOPLE = 18;
const ACCENT_PALETTE = ['#10B981', '#2563EB', '#0EA5E9', '#F59E0B', '#8B5CF6', '#EC4899'];

interface PromptPersonDraft {
  id: string;
  nodeId: string;
  name: string;
  title: string;
  reportsToRef: string | null;
  department?: string;
  email?: string;
}

export interface NormalizedPromptPerson {
  id: string;
  nodeId: string;
  name: string;
  title: string;
  reportsToId: string | null;
  department?: string;
  email?: string;
  accentColor: string;
}

export interface NormalizedPromptOrgChart {
  people: NormalizedPromptPerson[];
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function firstString(record: Record<string, unknown>, keys: string[]): string | null {
  for (const key of keys) {
    const value = record[key];
    if (typeof value === 'string' && value.trim()) return value.trim();
  }
  return null;
}

function normalizeLookupKey(value: string): string {
  return value
    .trim()
    .toLowerCase()
    .replace(/[_-]+/g, ' ')
    .replace(/[^a-z0-9]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

function slugify(value: string, fallback: string): string {
  const slug = value
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 48);
  return slug || fallback;
}

function uniqueId(base: string, used: Set<string>): string {
  let candidate = base;
  let suffix = 2;
  while (used.has(candidate)) {
    candidate = `${base}-${suffix}`;
    suffix += 1;
  }
  used.add(candidate);
  return candidate;
}

function wouldCreateCycle(parentByChild: Map<string, string>, childId: string, parentId: string): boolean {
  let cursor: string | undefined = parentId;
  while (cursor) {
    if (cursor === childId) return true;
    cursor = parentByChild.get(cursor);
  }
  return false;
}

function getPeopleArray(raw: unknown): unknown[] {
  if (!isRecord(raw)) return [];
  const people = raw.people;
  if (Array.isArray(people)) return people;
  const nodes = raw.nodes;
  if (Array.isArray(nodes)) return nodes;
  return [];
}

function extractJsonObject(text: string): unknown | null {
  const trimmed = text.trim();
  if (!trimmed) return null;

  const fenced = trimmed.match(/```(?:json)?\s*([\s\S]*?)```/i);
  const candidates = [
    fenced?.[1],
    trimmed,
    trimmed.slice(trimmed.indexOf('{'), trimmed.lastIndexOf('}') + 1),
  ].filter((candidate): candidate is string => Boolean(candidate?.trim()));

  for (const candidate of candidates) {
    try {
      return JSON.parse(candidate);
    } catch {
      /* try next candidate */
    }
  }
  return null;
}

export function normalizePromptOrgChartDraft(raw: unknown): NormalizedPromptOrgChart | null {
  const peopleArray = getPeopleArray(raw).slice(0, MAX_PROMPT_PEOPLE);
  const usedNodeIds = new Set<string>();
  const lookup = new Map<string, string>();

  const drafts: PromptPersonDraft[] = peopleArray
    .map((entry, index): PromptPersonDraft | null => {
      if (!isRecord(entry)) return null;
      const name = firstString(entry, ['name', 'label', 'fullName']);
      if (!name) return null;
      const rawId = firstString(entry, ['id', 'personId', 'employeeId']) ?? slugify(name, `person-${index + 1}`);
      const nodeId = uniqueId(`org-prompt-${slugify(rawId, `person-${index + 1}`)}`, usedNodeIds);
      const title = firstString(entry, ['title', 'role', 'position']) ?? (index === 0 ? 'CEO' : 'Team Member');
      const reportsToRef = firstString(entry, ['reportsTo', 'reports_to', 'managerId', 'manager', 'parentId']);
      const department = firstString(entry, ['department', 'team', 'function']);
      const email = firstString(entry, ['email', 'workEmail']);

      lookup.set(normalizeLookupKey(rawId), nodeId);
      lookup.set(normalizeLookupKey(nodeId), nodeId);
      lookup.set(normalizeLookupKey(name), nodeId);

      const draft: PromptPersonDraft = {
        id: rawId,
        nodeId,
        name,
        title,
        reportsToRef,
      };
      if (department) draft.department = department;
      if (email) draft.email = email;
      return draft;
    })
    .filter((person): person is PromptPersonDraft => Boolean(person));

  if (drafts.length === 0) return null;

  const parentByChild = new Map<string, string>();
  drafts.forEach((person) => {
    if (!person.reportsToRef) return;
    const parentId = lookup.get(normalizeLookupKey(person.reportsToRef));
    if (!parentId || parentId === person.nodeId) return;
    if (wouldCreateCycle(parentByChild, person.nodeId, parentId)) return;
    parentByChild.set(person.nodeId, parentId);
  });

  if (drafts.length > 1 && parentByChild.size === 0) {
    const rootId = drafts[0].nodeId;
    drafts.slice(1).forEach((person) => parentByChild.set(person.nodeId, rootId));
  }

  const hasRoot = drafts.some((person) => !parentByChild.has(person.nodeId));
  if (!hasRoot) {
    parentByChild.delete(drafts[0].nodeId);
  }

  return {
    people: drafts.map((person, index) => {
      const normalized: NormalizedPromptPerson = {
        id: person.id,
        nodeId: person.nodeId,
        name: person.name,
        title: person.title,
        reportsToId: parentByChild.get(person.nodeId) ?? null,
        accentColor: ACCENT_PALETTE[index % ACCENT_PALETTE.length],
      };
      if (person.department) normalized.department = person.department;
      if (person.email) normalized.email = person.email;
      return normalized;
    }),
  };
}

async function fetchPromptDraftViaAgent(prompt: string, requestFn: RequestFn): Promise<NormalizedPromptOrgChart | null> {
  const { streamAgentChatTurn } = await import('../../../core/ai/agent/api/agentApiClient');
  const { text } = await streamAgentChatTurn({
    endpoint: '/api/ai/agent/chat/stream',
    requestFn,
    body: {
      messages: [
        {
          role: 'system',
          content:
            'You are running a hidden org chart start-picker extraction step. ' +
            'No tools are available. Return JSON only, with no markdown or explanation.',
        },
        {
          role: 'user',
          content:
            'Convert this request into JSON shaped exactly like ' +
            '{"people":[{"id":"maya_chen","name":"Maya Chen","title":"CEO","reportsTo":null,"department":"Executive"}]}. ' +
            'Use null reportsTo for the root person. reportsTo must reference another id. ' +
            'Prefer 5-9 people unless the user explicitly asks for more.\n\n' +
            prompt,
        },
      ],
      diagramType: 'org',
      diagramSummary: {
        nodeCount: 0,
        edgeCount: 0,
        fields: ['name', 'title', 'reportsTo', 'department', 'email'],
      },
      availableTools: [],
      toolDefinitions: [],
    },
  });

  const raw = extractJsonObject(text);
  return normalizePromptOrgChartDraft(raw);
}

function computePromptReportCounts(people: NormalizedPromptPerson[]): Map<string, { directReports: number; totalReports: number }> {
  const childrenByParent = new Map<string, string[]>();
  people.forEach((person) => {
    if (!person.reportsToId) return;
    const children = childrenByParent.get(person.reportsToId) ?? [];
    children.push(person.nodeId);
    childrenByParent.set(person.reportsToId, children);
  });

  const counts = new Map<string, { directReports: number; totalReports: number }>();
  people.forEach((person) => {
    const directChildren = childrenByParent.get(person.nodeId) ?? [];
    const stack = [...directChildren];
    const visited = new Set<string>();
    while (stack.length > 0) {
      const childId = stack.pop();
      if (!childId || visited.has(childId)) continue;
      visited.add(childId);
      stack.push(...(childrenByParent.get(childId) ?? []));
    }
    counts.set(person.nodeId, {
      directReports: directChildren.length,
      totalReports: visited.size,
    });
  });
  return counts;
}

function writePromptGeneratedSettings(doc: Y.Doc | null | undefined): void {
  if (!doc) return;
  try {
    const map = doc.getMap('diagramSettings');
    const raw = map.get('settings');
    const settings = typeof raw === 'string' ? JSON.parse(raw) : {};
    settings.orgchart = {
      ...(settings.orgchart ?? {}),
      viewMode: 'compact',
      autoLayoutEnabled: true,
    };
    map.set('settings', JSON.stringify(settings));
  } catch (error) {
    console.warn('[orgPromptSeed] failed to write settings', error);
  }
}

async function fetchPromptDraft(prompt: string, requestFn: RequestFn): Promise<NormalizedPromptOrgChart | null> {
  const response = await requestFn('/api/ai/orgchart/generate-from-prompt', {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ text: prompt }),
  });

  if (!response.ok) {
    if (response.status === 404) {
      return fetchPromptDraftViaAgent(prompt, requestFn);
    }
    const message = await response.text().catch(() => 'Org chart prompt generation failed');
    throw new Error(message || 'Org chart prompt generation failed');
  }

  const raw = await response.json();
  return normalizePromptOrgChartDraft(raw);
}

function buildNativePromptScene(
  chart: NormalizedPromptOrgChart,
  edgeType: Edge['type'],
  defaultEdgeProps: Record<string, unknown>,
  targetDims: { width: number; height: number },
): { nodes: Node[]; edges: Edge[] } {
  const reportCounts = computePromptReportCounts(chart.people);
  const nodes: Node[] = chart.people.map((person, index) => {
    const counts = reportCounts.get(person.nodeId);
    const showFooter = (counts?.directReports ?? 0) > 0 || (counts?.totalReports ?? 0) > 0;
    const height = targetDims.height + (showFooter ? 22 : 0);
    const attrs: Record<string, unknown> = {
      personId: person.id,
      name: person.name,
      role: person.title,
      title: person.title,
      customFields: [],
      accentColor: person.accentColor,
    };
    if (person.department) attrs.department = person.department;
    if (person.email) attrs.email = person.email;

    return {
      id: person.nodeId,
      type: 'org_person_text',
      category: 'orgchart',
      name: person.name,
      x: 100 + (index % 4) * (targetDims.width + 80),
      y: 100 + Math.floor(index / 4) * (targetDims.height + 100),
      width: targetDims.width,
      height,
      w: targetDims.width,
      h: height,
      attrs,
    } as Node;
  });

  const edges: Edge[] = chart.people.flatMap((person) => {
    if (!person.reportsToId) return [];
    return [{
      id: `reports-to-${person.nodeId}-${person.reportsToId}`,
      type: edgeType,
      from: person.nodeId,
      to: person.reportsToId,
      fromAnchor: 'top',
      toAnchor: 'bottom',
      attrs: {
        ...defaultEdgeProps,
        relation: defaultEdgeProps.relation ?? 'reporting',
      },
    } as Edge];
  });

  return { nodes, edges };
}

function clearStarterSelection(useDiagramStore: typeof import('../../../stores/diagramStore').useDiagramStore): void {
  const clear = () => useDiagramStore.getState().setSelected(new Set());
  clear();
  if (typeof window !== 'undefined') {
    window.requestAnimationFrame(clear);
    window.setTimeout(clear, 0);
  }
}

function keepAssistantCollapsedForStarter(): void {
  if (typeof window === 'undefined') return;
  const collapse = () => window.dispatchEvent(new Event('assistant:collapse'));
  collapse();
  window.requestAnimationFrame(collapse);
  window.setTimeout(collapse, 250);
}

export async function generateOrgChartPromptSeed(
  commandManager: CommandManager,
  prompt: string,
  requestFn: RequestFn,
  doc?: Y.Doc | null,
): Promise<boolean> {
  const chart = await fetchPromptDraft(prompt, requestFn);
  if (!chart || chart.people.length === 0) return false;

  const [
    { getRelationshipMapping },
    { ORG_PERSON_CARD_SPECS },
    { OrgChartImportCommand },
    { LayoutOrgChartCommand },
    { computeFullTreeLayout },
    { useDiagramStore },
    { useOrgChartViewStore },
  ] = await Promise.all([
    import('../../../lib/diagram/registries'),
    import('../models/orgChartShapeVariants'),
    import('../commands/OrgChartImportCommand'),
    import('../commands/LayoutOrgChartCommand'),
    import('../services/graphAnalysis'),
    import('../../../stores/diagramStore'),
    import('../../../stores/orgchartViewStore'),
  ]);

  const mapping = getRelationshipMapping('org', 'reports_to');
  const edgeType = (mapping?.edgeType ?? 'reporting') as Edge['type'];
  const defaultEdgeProps = (mapping?.defaultEdgeProps ?? {}) as Record<string, unknown>;
  const scene = buildNativePromptScene(chart, edgeType, defaultEdgeProps, ORG_PERSON_CARD_SPECS.compact.card);
  if (scene.nodes.length === 0) return false;

  const viewStore = useOrgChartViewStore.getState();
  viewStore.clearCollapsed();
  viewStore.setAsOfDate(null);
  viewStore.setActiveViewpoint('none');
  viewStore.clearFocusScope();
  viewStore.setAutoLayoutEnabled(true);
  viewStore.setLastUsedOrgPersonNodeType('org_person_text');

  await commandManager.execute(new OrgChartImportCommand(scene.nodes, scene.edges));
  writePromptGeneratedSettings(doc);

  const latestNodes = useDiagramStore.getState().nodes;
  const latestEdges = useDiagramStore.getState().edges;
  const positions = computeFullTreeLayout(latestNodes, latestEdges) as Map<string, Point2D>;
  if (positions.size > 0) {
    await commandManager.execute(new LayoutOrgChartCommand({
      positions,
      edges: latestEdges,
      nodes: latestNodes,
      forceAnchorNormalization: true,
    }));
  }

  clearStarterSelection(useDiagramStore);
  viewStore.requestViewportFit();
  keepAssistantCollapsedForStarter();
  commandManager.clear();
  return true;
}
